import * as fileApi from '@/api/file';
import * as authApi from '@/api/auth';
import { FileInfoExtended } from '@/utils/fileStore';
import useFileStore from '@/utils/fileStore';
import {
    CaretRightOutlined as FolderClose,
    CaretDownOutlined as FolderOpen, LoadingOutlined
} from '@ant-design/icons';
import { App, Spin, Tree } from 'antd';
import type { TreeProps } from 'antd/es/tree';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FolderSelectorProps {
    onSelectDirectory: (path: string) => void;
    initialPath?: string;
}

const FolderSelector: React.FC<FolderSelectorProps> = ({ onSelectDirectory, initialPath }) => {
    const { message } = App.useApp();
    const [fileTree, setFileTree] = useState<FileInfoExtended | undefined>();
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { t } = useTranslation('common');

    // Initialize tree data
    useEffect(() => {
        initializeTreeData();
    }, []);

    // If initial path provided, expand to that path
    // useEffect(() => {
    //     if (initialPath && fileTree) {
    //         setSelectedKeys([initialPath]);
    //         onSelectDirectory(initialPath);

    //         setExpandedKeys(prev => [...new Set([...prev, initialPath])]);
    //     }
    // }, [initialPath, fileTree]);

    const initializeTreeData = async () => {
        try {
            setLoading(true);
            const userStateRes = await authApi.userIsReady();
            if(!userStateRes?.ok || !userStateRes.data?.isReady) {
                return;
            }
            const res = await fileApi.getFileResourceList({ paths: [], includeHidden: false });
            if (!res?.ok) {
                message.error(res?.message || t('folder_selector.init_failed'));
                return;
            }

            let treeData = res.data as FileInfoExtended;
            iconMapGenerator(treeData);
            setFileTree(treeData);

            // Initially select the base home directory
            if (res.data?.baseHome && !initialPath) {
                setSelectedKeys([res.data.baseHome]);
                onSelectDirectory(res.data.baseHome);
            }

        } catch (error) {
            console.error("Error initializing folder tree:", error);
            message.error(t('folder_selector.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    const openFolder = async (path: string) => {
        let loadingTimer: NodeJS.Timeout | null = null;

        loadingTimer = setTimeout(() => {
            setFileTree((prevTree) => {
                if (!prevTree) return prevTree;
                const updatedTree = { ...prevTree };
                const node = findNode(updatedTree, path);
                if (node) {
                    node.icon = <LoadingOutlined />;
                }
                return updatedTree;
            });
        }, 300);

        try {
            const res = await fileApi.getFileResource(path, useFileStore.getState().includeHidden);
            if (!res?.ok) {
                message.error(res?.message || t('folder_selector.open_failed'));
                return;
            }

            const folderData = res.data as FileInfoExtended;
            // Filter so we only keep directories
            if (folderData.children) {
                folderData.children = folderData.children.filter(item => item.isDir);
            }

            iconMapGenerator(folderData);

            setFileTree((prevTree) => {
                if (!prevTree) return prevTree;
                const updatedTree = { ...prevTree };
                const node = findNode(updatedTree, path);
                if (node) {
                    node.icon = <FolderOpen />;
                    node.children = folderData.children;
                }
                return updatedTree;
            });

            setExpandedKeys(prev => [...new Set([...prev, path])]);

        } catch (error) {
            console.error("Error opening folder:", error);
            message.error(t('folder_selector.open_failed'));
        } finally {
            if (loadingTimer) {
                clearTimeout(loadingTimer);
            }
        }
    };

    const closeFolder = (path: string) => {
        // Get all expanded keys that start with the closed path
        // This will include all subfolders
        const keysToRemove = expandedKeys.filter(key => 
            key === path || key.startsWith(path + '/')
        );
        
        setFileTree((prevTree) => {
            if (!prevTree) return prevTree;
            const updatedTree = { ...prevTree };
            
            // Update the icon for all nodes that are being collapsed
            keysToRemove.forEach(keyToRemove => {
                const node = findNode(updatedTree, keyToRemove);
                if (node) {
                    node.icon = <FolderClose />;
                }
            });
            
            return updatedTree;
        });

        // Remove all keys that belong to this folder and its subfolders
        setExpandedKeys(prev => prev.filter(key => !keysToRemove.includes(key)));
    };

    const handleSelect: TreeProps['onSelect'] = async (_selectedKeys, info) => {
        const selectedPath = info.node.key as string;

        // If already selected, toggle folder expansion
        if (expandedKeys.includes(selectedPath)) {
            closeFolder(selectedPath);
            return
        }

        try {
            setLoading(true);

            // First fetch the folder data
            const res = await fileApi.getFileResource(selectedPath, useFileStore.getState().includeHidden);
            if (!res?.ok) {
                message.error(res?.message || t('folder_selector.select_failed'));
                return;
            }

            const folderData = res.data as FileInfoExtended;
            folderData.children = folderData.children?.filter(item => item.isDir) as FileInfoExtended[] || [];
            iconMapGenerator(folderData);
            // Update the tree with fetched children and set icon to open
            setFileTree((prevTree) => {
                if (!prevTree) return prevTree;
                const updatedTree = { ...prevTree };
                const node = findNode(updatedTree, selectedPath);
                if (node) {
                    node.icon = <FolderOpen />;
                    node.children = folderData.children;
                }
                return updatedTree;
            });

            // Expand the selected folder
            setExpandedKeys(prev => [...new Set([...prev, selectedPath])]);

            // Update selected keys and notify parent component
            setSelectedKeys([selectedPath]);
            onSelectDirectory(selectedPath);
        }
        catch (error) {
            console.error("Error selecting folder:", error);
            message.error(t('folder_selector.select_failed'));
        }
        finally {
            setLoading(false);
        }

    };

    const handleExpand: TreeProps['onExpand'] = (_expandedKeys, info) => {
        const path = info.node.key as string;

        if (info.expanded) {
            openFolder(path);
        } else {
            closeFolder(path);
        }
    };

    // Helper functions
    const findNode = (node: FileInfoExtended, targetPath: string): FileInfoExtended | undefined => {
        if (node.path === targetPath) {
            return node;
        }

        if (node.children) {
            for (const child of node.children) {
                const result = findNode(child, targetPath);
                if (result) {
                    return result;
                }
            }
        }

        return undefined;
    };

    const iconMapGenerator = (fileTree: FileInfoExtended) => {
        const traverse = (node: FileInfoExtended) => {
            if (node.children) {
                node.children.forEach((child) => {
                    traverse(child);
                });
            }

            if (!node?.icon) {
                if (node.isDir) {
                    if (expandedKeys?.includes(node.path)) {
                        node.icon = <FolderOpen />;
                    } else {
                        node.icon = <FolderClose />;
                    }
                }
            }
        };
        traverse(fileTree);
    };

    if (!fileTree) {
        return <div className="p-4 text-center">Loading...</div>;
    }

    // Filter tree data to only show directories
    const filteredTreeData = fileTree.children?.filter(item => item.isDir) || [];

    return (
        <div className="folder-selector h-full overflow-auto">
            <Spin spinning={loading}>
                <Tree
                    showIcon={true}
                    treeData={filteredTreeData}
                    fieldNames={{ title: 'name', children: 'children', key: 'path' }}
                    blockNode={true}
                    switcherIcon={<></>}
                    selectedKeys={selectedKeys}
                    expandedKeys={expandedKeys}
                    onExpand={handleExpand}
                    onSelect={handleSelect}
                    virtual={false}
                />
            </Spin>
        </div>
    );
};

export default FolderSelector;
